import { Link } from "react-router-dom";
import {
  ArrowRight, Gauge, Cpu, Zap as ZapBolt, Leaf,
  Volume2, MapPin, Headphones, Wrench,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import Reveal from "@/components/layout/Reveal";
import SectionLabel from "@/components/layout/SectionLabel";

export const WhyWorkWithUsSection = () => {
  return (
    <section data-testid="why-work-with-us" className="py-20 sm:py-28 bg-white relative overflow-hidden">
      <div className="absolute -top-32 -left-32 h-[420px] w-[420px] rounded-full bg-[#10B981]/8 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 sm:px-8 relative">
        <Reveal className="max-w-3xl">
          <SectionLabel>Why work with us</SectionLabel>
          <h2 className="font-heading mt-4 text-3xl sm:text-5xl font-bold tracking-tight text-[#0B132B] leading-[1.05]">
            Premium technology. <span className="text-[#10B981]">Local expertise.</span>
          </h2>
          <p className="mt-5 text-lg text-[#475569] leading-relaxed">
            Eight reasons Kiwi homeowners are choosing AeroMax Energy and the NeoPower Black Shield Series for their hot water upgrade.
          </p>
        </Reveal>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {[
            {
              icon: Gauge,
              title: "COP up to 5.4",
              desc: "Industry-leading efficiency rating — more hot water for every kW of electricity.",
            },
            {
              icon: Cpu,
              title: "DC Full Inverter",
              desc: "Variable-speed compressor adapts to demand for smoother, quieter operation.",
            },
            {
              icon: ZapBolt,
              title: "Energy Efficient",
              desc: "Save up to 80% on hot water energy versus traditional electric or gas systems.",
            },
            {
              icon: Leaf,
              title: "R290 Refrigerant",
              desc: "Natural refrigerant with very low global warming impact — climate-friendly by design.",
            },
            {
              icon: Volume2,
              title: "Quiet Operation",
              desc: "Silent-mode running at 42 dBa — discreet enough for almost any install location.",
            },
            {
              icon: MapPin,
              title: "Local Experts",
              desc: "Founded by NZ-based smart-home and energy-efficiency specialists with 10+ years of experience.",
            },
            {
              icon: Headphones,
              title: "NZ-Focused Support",
              desc: "Real Kiwi customer service — quotes, installations and ongoing care from people who know NZ homes.",
            },
            {
              icon: Wrench,
              title: "Professional Installation",
              desc: "Certified plumbers and electricians coordinated through our trusted installer network.",
            },
          ].map((c, i) => (
            <Reveal key={i} delay={i * 50}>
              <Card
                data-testid={`why-card-${i}`}
                className="group relative h-full p-6 rounded-2xl border-[#E2E8F0] bg-white hover:border-[#10B981]/40 hover:shadow-xl hover:shadow-emerald-500/8 hover:-translate-y-1 transition-all duration-300"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#0B132B] text-[#10B981] mb-4 group-hover:bg-[#10B981] group-hover:text-white transition-colors">
                  <c.icon className="h-5 w-5" strokeWidth={1.8} />
                </span>
                <p className="font-heading text-[17px] font-semibold text-[#0B132B] leading-tight">
                  {c.title}
                </p>
                <p className="text-sm text-[#475569] mt-2 leading-relaxed">
                  {c.desc}
                </p>
              </Card>
            </Reveal>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap items-center gap-3">
          <Link
            to="/free-home-assessment"
            className="inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-7 py-3.5 font-semibold transition-all hover:shadow-lg hover:shadow-emerald-500/30"
          >
            Request a Free Assessment <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            to="/installation"
            className="inline-flex items-center gap-2 rounded-full border-2 border-[#0B132B] text-[#0B132B] hover:bg-[#0B132B] hover:text-white px-7 py-3.5 font-semibold transition-all"
          >
            See Installation Process
          </Link>
        </div>
      </div>
    </section>
  );
};
